import { Leitura, Variavel } from "../../api/m5";

function escapeHtml(valor: string): string {
  return valor
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const CARDEAIS = ["N", "NNE", "NE", "ENE", "L", "ESE", "SE", "SSE", "S", "SSO", "SO", "OSO", "O", "ONO", "NO", "NNO"];

function cardinal(graus: number): string {
  const normalizado = ((graus % 360) + 360) % 360;
  return CARDEAIS[Math.round(normalizado / 22.5) % 16];
}

function formatarValor(valor: number): string {
  return Number.isInteger(valor) ? String(valor) : valor.toFixed(2);
}

function formatarTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

// Direcao meteorologica = de onde o vento vem. A agulha e desenhada apontando
// pra essa direcao (convencao de cata-vento), com o angulo/cardeal tambem
// escrito por extenso na legenda pra nao depender so da leitura do desenho.
function rosaVentoSvg(direcaoGraus: number): string {
  const angulo = ((direcaoGraus % 360) + 360) % 360;
  return `
    <svg viewBox="0 0 120 120" width="108" height="108" class="rosa-ventos" role="img" aria-label="Direção do vento: ${cardinal(angulo)}, ${Math.round(angulo)} graus">
      <circle cx="60" cy="60" r="52" fill="none" stroke="var(--color-border, #e2e8f0)" stroke-width="2"/>
      <text x="60" y="15" text-anchor="middle" class="rosa-ventos__cardeal">N</text>
      <text x="105" y="64" text-anchor="middle" class="rosa-ventos__cardeal">L</text>
      <text x="60" y="111" text-anchor="middle" class="rosa-ventos__cardeal">S</text>
      <text x="15" y="64" text-anchor="middle" class="rosa-ventos__cardeal">O</text>
      <g transform="rotate(${angulo} 60 60)">
        <line x1="60" y1="60" x2="60" y2="20" stroke="var(--color-primary, #2D9AC3)" stroke-width="4" stroke-linecap="round"/>
        <polygon points="60,10 51,28 69,28" fill="var(--color-primary, #2D9AC3)"/>
      </g>
      <circle cx="60" cy="60" r="5" fill="var(--color-primary, #2D9AC3)"/>
    </svg>`;
}

export function detalhesCarregando(nomeEstacao: string): string {
  return `
    <div class="estacao-detalhes">
      <h4>${escapeHtml(nomeEstacao)}</h4>
      <p class="aviso">Carregando dados…</p>
    </div>`;
}

export function detalhesErro(nomeEstacao: string): string {
  return `
    <div class="estacao-detalhes">
      <h4>${escapeHtml(nomeEstacao)}</h4>
      <p class="aviso">Não foi possível carregar os dados desta estação.</p>
    </div>`;
}

/**
 * Mantem so a leitura mais recente por (tag, indice). Assume que `leituras`
 * ja vem ordenada DESC por timestamp_medicao - comportamento padrao de
 * GET /m5/api/leituras quando chamado sem agregacao (ver leituras.ts do M5).
 */
export function ultimaLeituraPorVariavel(leituras: Leitura[]): Leitura[] {
  const vistos = new Map<string, Leitura>();
  for (const l of leituras) {
    const chave = `${l.tag}_${l.indice}`;
    if (!vistos.has(chave)) vistos.set(chave, l);
  }
  return [...vistos.values()];
}

// Tags de vento usadas nas estacoes meteorologicas do projeto (ver
// modules/m10/dadds-noaa/scripts/epamig_noaa.py) - US = velocidade (m/s),
// UD = direcao (graus). Estacao sem essas tags simplesmente nao mostra a
// rosa dos ventos, so a lista de variaveis normal.
const TAG_DIRECAO_VENTO = "UD";
const TAG_VELOCIDADE_VENTO = "US";

export function montarConteudoDetalhes(nomeEstacao: string, leituras: Leitura[], variaveis: Variavel[]): string {
  if (leituras.length === 0) {
    return `
      <div class="estacao-detalhes">
        <h4>${escapeHtml(nomeEstacao)}</h4>
        <p class="aviso">Nenhum dado disponível para esta estação.</p>
      </div>`;
  }

  const porTag = new Map(variaveis.map((v) => [v.tag, v]));
  const nomeVariavel = (tag: string) => porTag.get(tag)?.nome || tag;
  const unidadeVariavel = (tag: string) => porTag.get(tag)?.unidade || "";

  const ultimaAtualizacao = leituras.reduce(
    (max, l) => (l.timestamp_medicao > max ? l.timestamp_medicao : max),
    leituras[0].timestamp_medicao
  );

  const direcao = leituras.find((l) => l.tag === TAG_DIRECAO_VENTO && l.indice === 1);
  const velocidade = leituras.find((l) => l.tag === TAG_VELOCIDADE_VENTO && l.indice === 1);

  let blocoVento = "";
  if (direcao) {
    blocoVento = `
      <div class="estacao-detalhes__vento">
        ${rosaVentoSvg(direcao.valor)}
        <div class="estacao-detalhes__vento-legenda">
          ${velocidade ? `<strong>${formatarValor(velocidade.valor)} ${escapeHtml(unidadeVariavel(TAG_VELOCIDADE_VENTO))}</strong>` : ""}
          <span>vento de ${cardinal(direcao.valor)} (${Math.round(direcao.valor)}°)</span>
        </div>
      </div>`;
  }

  const linhas = leituras
    .filter((l) => l !== direcao && l !== velocidade)
    .map((l) => ({ leitura: l, rotulo: nomeVariavel(l.tag) }))
    .sort((a, b) => a.rotulo.localeCompare(b.rotulo))
    .map(
      ({ leitura, rotulo }) =>
        `<tr><td>${escapeHtml(rotulo)}</td><td>${formatarValor(leitura.valor)} ${escapeHtml(unidadeVariavel(leitura.tag))}</td></tr>`
    )
    .join("");

  return `
    <div class="estacao-detalhes">
      <h4>${escapeHtml(nomeEstacao)}</h4>
      <p class="estacao-detalhes__timestamp">Última leitura: ${formatarTimestamp(ultimaAtualizacao)}</p>
      ${blocoVento}
      <table class="estacao-detalhes__tabela"><tbody>${linhas}</tbody></table>
    </div>`;
}
